"use client";

import React, { useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { ArrowRight } from "lucide-react";
import Script from "next/script";

const HERO_IMAGES = {
  main: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  dental: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  pt: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  legal: 'https://images.unsplash.com/photo-1589829545856-d10d727437a1?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  hvac: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  podiatry: 'https://images.unsplash.com/photo-1584433134234-2a4d2e4e4f77?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
} as const;

interface IndustryConfig {
  name: string;
  title: string;
  subtitle: string;
  description: string;
  heroDescription: string;
  demoTitle?: string;
  demoDescription?: string;
  benefits: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  useCases: string[];
  widgetId?: string;
  formTitle?: string;
  formDescription?: string;
  vertical?: string;
  heroGradient?: boolean;
  heroTagline?: string;
  heroSubHeadline?: string;
  heroCTA1?: string;
  heroCTA2?: string;
  heroFooter?: string;
  stats?: Array<{
    value: string;
    label: string;
  }>;
  testimonials?: Array<{
    quote: string;
    author: string;
    title?: string;
  }>;
  pricing?: {
    title: string;
    plans: Array<{
      name: string;
      price: string;
      period: string;
      features: string[];
      cta: string;
      ctaLink: string;
      footer?: string;
    }>;
    footer?: string;
  };
}

interface IndustryLandingProps {
  config: IndustryConfig;
}

export default function IndustryLanding({ config }: IndustryLandingProps) {
  const [formData, setFormData] = useState({
    name: "",
    practiceType: config.name,
    websiteUrl: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/api/purposeai/lead", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          practiceType: formData.practiceType,
          websiteUrl: formData.websiteUrl,
          phone: formData.phone,
          vertical: config.vertical,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit form");
      }

      setSubmitted(true);
      setFormData({
        name: "",
        practiceType: config.name,
        websiteUrl: "",
        phone: "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit form. Please try again.");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuizClick = () => {
    document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Full-Width Hero with Background */}
      <header 
        className="relative min-h-screen flex items-center justify-center text-center overflow-hidden bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700"
      >
        {/* Gradient overlay fallback */}
        <div className="absolute inset-0 bg-gradient-to-r from-teal-800/20 to-emerald-800/20"></div>
        
        {/* Background image overlay */}
        {(() => {
          const vertical = config.vertical || config.name.toLowerCase();
          const imageUrl = config.heroBackgroundImage || HERO_IMAGES[vertical as keyof typeof HERO_IMAGES] || HERO_IMAGES.main;
          return (
            <>
              <div className="absolute inset-0 bg-black opacity-40"></div>
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                style={{ backgroundImage: `url('${imageUrl}')` }}
              ></div>
            </>
          );
        })()}

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-24">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            {config.heroTagline || config.title}
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-white/95 mb-12 max-w-4xl mx-auto">
            {config.heroSubHeadline || "AI coworkers that work alongside your team — never replace them"}
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="#demo-form"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-amber-500 hover:bg-amber-400 text-white px-10 py-5 rounded-xl text-xl md:text-2xl font-bold transition"
            >
              {config.heroCTA1 || "Get My Custom AI Demo (3 min)"}
            </a>
            {config.heroCTA2 && (
              <a
                href="#demo"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border-4 border-white hover:bg-white hover:text-teal-600 text-white px-10 py-5 rounded-xl text-xl md:text-2xl font-bold transition"
              >
                {config.heroCTA2}
              </a>
            )}
          </div>
        </div>

        {/* Wave SVG at bottom */}
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path fill="#f8fafc" fillOpacity="1" d="M0,96L48,90.7C96,85,192,75,288,69.3C384,64,480,64,576,74.7C672,85,768,107,864,106.7C960,107,1056,85,1152,80C1248,75,1344,85,1392,90.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </header>

      {/* Main Content - Full Width */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Why PurposeAI Section */}
        <section className="py-12 sm:py-16 text-center" style={{ background: '#f8fafc' }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5" style={{ color: '#0D9488' }}>
            Why PurposeAI Feels Different
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10 px-4">
            Your mission stays human. The grunt work doesn't.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto px-4">
            {config.benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="mb-4 text-4xl">{benefit.icon}</div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#0D9488' }}>
                  {benefit.title}
                </h3>
                <p className="text-gray-700">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* See In Action Section */}
        {config.widgetId && (
          <section id="demo" className="py-16 sm:py-20 text-center bg-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0D9488', fontSize: '2.8rem' }}>
              {config.demoTitle || "See PurposeAI Book a Real Patient Right Now"}
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-10 px-4" style={{ fontSize: '1.3rem' }}>
              {config.demoDescription || config.heroDescription}
            </p>

            {/* Stats Section */}
            {config.stats && config.stats.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10 max-w-4xl mx-auto px-4 text-left">
                {config.stats.map((stat, index) => (
                  <div key={index}>
                    <strong className="text-2xl sm:text-3xl font-bold" style={{ color: '#0D9488' }}>{stat.value}</strong>
                    <br />
                    <span className="text-sm sm:text-base text-gray-600">{stat.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div 
              id="widget-demo-container"
              className="mx-auto rounded-2xl overflow-hidden shadow-2xl bg-white relative"
              style={{
                maxWidth: '900px',
                minHeight: '650px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              }}
            >
              <iframe
                id="purposeai-demo-iframe"
                src={`https://app.easygpt.builders/w/${config.widgetId}?full=true&scroll=top`}
                width="100%"
                height="650px"
                title="Live PurposeAI Receptionist Demo"
                frameBorder="0"
                allow="microphone; camera"
                className="w-full"
                style={{ minHeight: '650px' }}
              />
              
              <div 
                id="widget-fallback"
                className="hidden absolute inset-0 flex items-center justify-center bg-gray-50 p-8 text-center rounded-2xl"
              >
                <div>
                  <p className="text-lg text-gray-700 mb-4 font-semibold">
                    Live Demo Temporarily Unavailable
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    The widget is experiencing technical difficulties. Please fill out the form below to see your custom AI receptionist demo.
                  </p>
                  <Button
                    onClick={() => {
                      document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-white"
                    style={{ background: '#0D9488' }}
                  >
                    Get My Custom Demo
                  </Button>
                </div>
              </div>
            </div>

            {config.widgetId && (
              <Script
                src={`https://app.easygpt.builders/w.js/?id=${config.widgetId}&v=v20251104.4`}
                strategy="afterInteractive"
              />
            )}

            {/* Second CTA - Direct action after demo */}
            <div className="text-center mt-10">
              <button
                onClick={() => {
                  document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-xl px-10 py-6 rounded-2xl shadow-2xl transform hover:scale-105 transition"
              >
                Hire this AI coworker for your practice →
              </button>
              <p className="mt-4 text-gray-600">
                Takes 3 minutes · 14-day free trial · No employees replaced
              </p>
            </div>

            <p className="mt-8 text-gray-600 text-base sm:text-lg px-4 max-w-2xl mx-auto">
              ↑ This exact AI will be customized with <strong>your</strong> {config.vertical === 'legal' ? 'practice areas, fees, and intake process' : 'website, pricing, calendar, and mission'} in &lt;48 hours.
            </p>
          </section>
        )}

        {/* Testimonials Section */}
        {config.testimonials && config.testimonials.length > 0 && (
          <section className="py-12 sm:py-16" style={{ background: '#f8fafc' }}>
            <h2 className="text-center text-3xl sm:text-4xl font-bold mb-10" style={{ color: '#0D9488' }}>
              {config.name === 'Dental' ? 'Dentists Who Scaled With PurposeAI' : 'Success Stories'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto px-4">
              {config.testimonials.map((testimonial, index) => (
                <blockquote
                  key={index}
                  className="bg-white p-6 rounded-lg shadow-sm"
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                >
                  <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                  <cite className="font-bold block mt-3" style={{ color: '#0D9488' }}>
                    {testimonial.author}
                    {testimonial.title && <span className="block text-sm font-normal text-gray-600 mt-1">{testimonial.title}</span>}
                  </cite>
                </blockquote>
              ))}
            </div>
          </section>
        )}

        {/* Pricing Section */}
        {config.pricing ? (
          <section className="py-12 sm:py-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10" style={{ color: '#0D9488' }}>
              {config.pricing.title}
            </h2>
            <div className="flex flex-wrap justify-center gap-10 px-4">
              {config.pricing.plans.map((plan, index) => (
                <div
                  key={index}
                  className="border-2 rounded-xl p-8 max-w-sm"
                  style={{ borderColor: '#0D9488' }}
                >
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#0D9488' }}>
                    {plan.name}
                  </h3>
                  <div className="text-5xl font-bold mb-4" style={{ color: '#0D9488' }}>
                    {plan.price}
                    <span className="text-xl font-normal">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-6 font-semibold">One flat price. Unlimited calls. Zero humans replaced.</p>
                  <ul className="text-left my-6 space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2" style={{ color: '#0D9488' }}>✓</span>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#demo-form"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="block w-full py-4 px-6 rounded-lg font-bold text-white text-center transition-colors"
                    style={{ background: '#0D9488' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#0b7d73'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#0D9488'}
                  >
                    {plan.cta}
                  </a>
                  {plan.footer && (
                    <p className="text-sm text-gray-600 mt-4">{plan.footer}</p>
                  )}
                </div>
              ))}
            </div>
            {config.pricing.footer && (
              <p 
                className="mt-8 text-gray-600"
                dangerouslySetInnerHTML={{ __html: config.pricing.footer }}
              />
            )}
          </section>
        ) : (
          <section className="text-center my-12 sm:my-16">
            <p className="text-base sm:text-lg text-gray-700 mb-6">
              Works for: {config.useCases.map((useCase, i) => (
                <React.Fragment key={i}>
                  <strong>{useCase}</strong>
                  {i < config.useCases.length - 1 && " · "}
                </React.Fragment>
              ))}
            </p>
            <div className="my-8 space-y-2">
              <p className="text-lg sm:text-xl text-gray-800">
                Pricing: <strong style={{ color: '#0D9488' }}>$399/mo</strong> — unlimited calls + chats
              </p>
              <p className="text-base sm:text-lg text-gray-700">
                <strong>No employees replaced — ever.</strong>
              </p>
            </div>
          </section>
        )}

        {/* Demo Form */}
        <section className="py-12 sm:py-16 text-center" style={{ background: '#f8fafc' }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5" style={{ color: '#0D9488' }}>
            {config.formTitle || "Ready to Get Started?"}
          </h2>
          <form 
            id="demo-form"
            onSubmit={handleSubmit}
            className="max-w-lg mx-auto space-y-4"
          >
            {config.vertical && (
              <input type="hidden" name="vertical" value={config.vertical} />
            )}
            <Input
              type="text"
              name="name"
              placeholder={config.vertical === 'legal' ? "Your Name" : "Name"}
              value={formData.name}
              onChange={handleInputChange}
              className="w-full h-12 text-base"
              required
              disabled={isSubmitting || submitted}
            />
            <Input
              type="text"
              name="practiceType"
              placeholder={config.vertical === 'legal' ? "Firm Name or Practice Area (e.g., PI, Family Law)" : "Practice Type"}
              value={formData.practiceType}
              onChange={handleInputChange}
              className="w-full h-12 text-base"
              required
              disabled={isSubmitting || submitted}
            />
            <Input
              type="url"
              name="websiteUrl"
              placeholder="Website URL"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              className="w-full h-12 text-base"
              required
              disabled={isSubmitting || submitted}
            />
            <Input
              type="tel"
              name="phone"
              placeholder={config.vertical === 'legal' ? "Phone or Email" : "Phone"}
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full h-12 text-base"
              required
              disabled={isSubmitting || submitted}
            />
            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full text-white px-6 py-4 text-lg rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: config.vertical === 'legal' ? '#0D9488' : '#F59E0B' }}
              onMouseEnter={(e) => {
                if (!isSubmitting && !submitted) {
                  e.currentTarget.style.background = config.vertical === 'legal' ? '#0b7d73' : '#d97706';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && !submitted) {
                  e.currentTarget.style.background = config.vertical === 'legal' ? '#0D9488' : '#F59E0B';
                }
              }}
              size="lg"
              disabled={isSubmitting || submitted}
            >
              {isSubmitting ? "Sending..." : submitted ? "Submitted!" : config.vertical === 'legal' ? "Get My Custom Legal AI Demo →" : "Get My Custom Demo →"}
            </Button>
            {submitted && (
              <p className="text-center mt-4" style={{ color: '#0D9488' }}>
                Thanks! We'll email your custom AI blueprint in 24h. Questions? Reply here.
              </p>
            )}
            {config.formDescription && (
              <p className="mt-5 text-gray-500 text-sm">
                {config.formDescription}
              </p>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}

